import { Routes, UrlMatchResult, UrlSegment } from '@angular/router';

// One matcher for /training[/:app[/:id]] so the component is reused across tab changes.
export function trainingMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length > 2) return null;
  const posParams: Record<string, UrlSegment> = {};
  if (segments[0]) posParams['app'] = segments[0];
  if (segments[1]) posParams['id'] = segments[1];
  return { consumed: segments, posParams };
}
import { LoginComponent } from './login/login.component';
import { SsoComponent } from './sso/sso.component';
import { F5500ProductShelfComponent } from './f5500-product-shelf/f5500-product-shelf.component';
import { AuthService } from './services/auth-service.service';
import { UnifiedFlowSearchComponent } from './unified-flow/unified-flow-search/unified-flow-search.component';
import { UnifiedCaseComponent } from './unified-flow/unified-case/unified-case.component';
import { PostbackThankYouComponent } from './shared-components/postback-thank-you/postback-thank-you.component';
import { ProxyComponent } from './proxy/proxy.component';
import { ToolsPageComponent } from './tools-page/tools-page.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { PageNotAllowedComponent } from './page-not-allowed/page-not-allowed.component';
import { NoCdrRequiredComponent } from './no-cdr-required/no-cdr-required.component';
import { LandingPageComponent } from './unified-flow/landing-page/landing-page.component';
import { UserSettingsComponent } from './unified-flow/user-settings/user-settings.component';
import { CaseExplorerComponent } from './case-explorer/case-explorer.component';
import { RepBehaviorComponent } from './rep-behavior/rep-behavior.component';
import { RbChatbotComponent } from './rb-chatbot/rb-chatbot.component';
import { ProfileHistoryComponent } from './admin/profile-history/profile-history.component';
import { LegacyProfileHistoryComponent } from './admin/legacy-profile-history/legacy-profile-history.component';
import { CustomCompletedPageComponent } from './shared-components/custom-completed-page/custom-completed-page.component';
import { AutoReportsComponent } from './auto-reports/auto-reports.component';
import { TemplatesComponent } from './account-management/templates/templates.component';
import { ProductGridComponent } from './investment-wizard/product-grid/product-grid.component';
import { IWProductSearchComponent } from './investment-wizard/product-search/product-search.component';
import { AWProductSearchComponent } from './annuity-wizard/product-search/product-search.component';
import { AppGlobalsService } from './services/app-globals.service';
import { ProductShelfComponent } from './annuity-wizard/product-shelf/product-shelf.component';
import { RmdCalculationComponent } from './rmd-calculation/rmd-calculation/rmd-calculation.component';
import { AwComparisonComponent } from './unified-flow/aw-comparison/aw-comparison.component';
import { PlanLookupComponent } from './product-profiler/plan-lookup/plan-lookup.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { CaseManagementComponent } from './case-management/case-management-shell/case-management.component';
import { CaseManagerComponent } from './case-management/case-manager/case-manager.component';
import { BookManagementComponent } from './book-management/book-management-shell/book-management.component';
import { CarrierPortalComponent } from './carrier-portal/carrier-portal.component';
import { ContentManagementComponent } from './admin/content-management/content-management.component';
import { KbChangeHistoryComponent } from './admin/kb-change-history/kb-change-history.component';
import { RepManagementComponent } from './admin/rep-management/rep-management.component';
import { RolesRightsComponent } from './admin/roles-rights/roles-rights.component';
import { ToolboxComponent } from './admin/toolbox/toolbox.component';
import { UnitManagementComponent } from './admin/unit-management/unit-management.component';
import { UnitProfileComponent } from './admin/unit-profile/unit-profile.component';
import { UploadsComponent } from './admin/uploads/uploads.component';
import { UserManagementComponent } from './admin/user-management/user-management.component';
import { VersionsComponent } from './admin/versions/versions.component';
import { TrainingFlowComponent } from './training/training-flow/training-flow.component';
import { AccountGroupingComponent } from './post-trade/account-grouping/account-grouping-shell/account-grouping.component';
import { AccountLinkingComponent } from './post-trade/account-linking/account-linking-shell/account-linking.component';
import { AccountReviewComponent } from './post-trade/account-review/account-review-shell/account-review.component';
import { AmlMonitorComponent } from './post-trade/aml-monitor/aml-monitor-shell/aml-monitor.component';
import { PostTradeReportsComponent } from './post-trade/post-trade-reports/post-trade-reports.component';
import { PostTradeComponent } from './post-trade/post-trade-shell/post-trade.component';
import { RepReviewComponent } from './post-trade/rep-review/rep-review-shell/rep-review.component';
import { TradeReviewComponent } from './post-trade/trade-review/trade-review-shell/trade-review.component';
import { TradeSourceDataComponent } from './post-trade/trade-source-data/trade-source-data.component';
import { UnifiedReportingComponent } from './reporting/unified-reporting/unified-reporting.component';
import { DownloadPageComponent } from './downloads/download-page/download-page.component';
import { FormlyComponent } from './conditional-form/formly.component';
import { ProductResultComponent } from './shared-components/product-result/product-result.component';
import { ResultDetailComponent } from './shared-components/result-detail/result-detail.component';
import { UniversalSearchComponent } from './universal-search/universal-search/universal-search.component';
import { CannexCalcComponent } from './annuity-wizard/cannex-calc/cannex-calc.component';
import { CashFlowPlannerComponent } from './cash-flow-planner/cash-flow-planner/cash-flow-planner.component';
import { HotRatesComponent } from './annuity-wizard/hot-rates/hot-rates.component';

export const appRoutes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    component: LandingPageComponent,
    canActivate: [AuthService],
  },
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'login/:session/:org',
    component: SsoComponent,
  },
  {
    path: 'login/:session/:org/profile-error',
    component: SsoComponent,
  },
  {
    path: 'login/:session/:org/login/error/:correlationId',
    component: SsoComponent,
  },
  {
    path: 'login/:session/:org/training/:app/:video',
    component: SsoComponent,
  },
  {
    path: 'login/:session/:org/:app',
    component: SsoComponent,
  },
  {
    path: 'login/:session/:org/:app/:profile',
    component: SsoComponent,
  },
  {
    path: 'searchLogin/:session/:org/:app/:search',
    component: SsoComponent,
  },
  {
    path: 'searchLogin/:session/:org/:app/:search/:bridgeId',
    component: SsoComponent,
  },
  {
    path: 'regions/login/:session/:org',
    component: SsoComponent,
  },
  {
    path: 'regions/login/:session/:org/:app',
    component: SsoComponent,
  },
  {
    path: 'regions/login/:session/:org/:app/:profile',
    component: SsoComponent,
  },
  {
    path: 'rb-chatbot/:profileId',
    component: RbChatbotComponent,
    canActivate: [AuthService],
    data: {},
  },
  {
    path: 'rb-chatbot',
    component: RbChatbotComponent,
    canActivate: [AuthService],
    data: {
      rightNeeded: {
        rights: ['AIChatbotOnHomeScreen', 'AIChatbotOnQuestionnaire'],
        operation: 'or',
      },
    },
  },
  {
    path: 'dashboard',
    canActivate: [AuthService],
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        component: DashboardComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'NewDashboardTab',
          breadcrumb: 'Dashboard',
        },
      },
    ],
  },
  {
    path: 'rep-behavior',
    component: RepBehaviorComponent,
    canActivate: [AuthService],
    data: {
      rightNeeded: 'RepBehaviorPage',
    },
  },
  {
    path: 'case-explorer',
    component: CaseExplorerComponent,
    canActivate: [AuthService],
    data: {
      rightNeeded: 'CaseExplorer',
    },
  },
  {
    path: 'legacy-profile-history/:solution/:profileId',
    component: LegacyProfileHistoryComponent,
    canActivate: [AuthService],
    data: {
      rightNeeded: 'NewDashboardTab',
    },
  },
  {
    path: 'profile-history/:solution/:profileId',
    component: ProfileHistoryComponent,
    canActivate: [AuthService],
    data: {
      rightNeeded: 'CaseExplorer',
    },
  },
  {
    path: 'f5500',
    component: F5500ProductShelfComponent,
    canActivate: [AuthService],
    data: {
      rightNeeded: 'PlanLookupTool',
    },
  },
  {
    path: 'iw',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: 'templates',
        component: TemplatesComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'IW',
        },
      },
      {
        path: 'product-shelf',
        component: ProductGridComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'IW',
        },
      },
      {
        path: 'product-shelf/:program',
        component: ProductGridComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'IW',
        },
      },
      {
        path: 'lookup',
        component: IWProductSearchComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'IW',
        },
      },
      {
        path: '',
        redirectTo: '/',
        pathMatch: 'full',
      },
      // THE FOLLOWING ARE OLD ROUTES THAT HAVE BEEN
      // REDIRECTED TO THE LANDING PAGE
      {
        path: 'search',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'questionnaire',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'questionnaire/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'investment-model/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'program-selection/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'fund-allocations/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'validate/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'advisory/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'help',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'proxy',
        redirectTo: '/',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'aw',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: 'product-shelf',
        component: ProductShelfComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'ProductShelf',
        },
      },
      {
        path: 'product-shelf/:viewOption',
        component: ProductShelfComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'ProductShelf',
        },
      },
      {
        path: 'product-shelf/:viewOption/:cusip',
        component: ProductShelfComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'ProductShelf',
        },
      },
      {
        path: 'product-shelf/:viewOption/:session/:org',
        component: ProductShelfComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'ProductShelf',
        },
      },
      {
        path: 'product-shelf/:viewOption/:session/:org/:cusip',
        component: ProductShelfComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'ProductShelf',
        },
      },
      {
        path: 'rila',
        redirectTo: '/aw/product-shelf/rila',
        pathMatch: 'full',
      },
      {
        path: 'rila/:cusip',
        redirectTo: '/aw/product-shelf/rila/:cusip',
        pathMatch: 'full',
      },
      {
        path: 'rila/:session/:org',
        redirectTo: '/aw/product-shelf/rila',
        pathMatch: 'full',
      },
      {
        path: 'rila/:session/:org/:cusip',
        redirectTo: '/aw/product-shelf/rila/:cusip',
        pathMatch: 'full',
      },
      {
        path: 'fia',
        redirectTo: '/aw/product-shelf/fia',
        pathMatch: 'full',
      },
      {
        path: 'fia/:cusip',
        redirectTo: '/aw/product-shelf/fia/:cusip',
        pathMatch: 'full',
      },
      {
        path: 'fia/:session/:org',
        redirectTo: '/aw/product-shelf/fia',
        pathMatch: 'full',
      },
      {
        path: 'fia/:session/:org/:cusip',
        redirectTo: '/aw/product-shelf/fia/:cusip',
        pathMatch: 'full',
      },
      {
        path: 'fa',
        redirectTo: '/aw/product-shelf/fa',
        pathMatch: 'full',
      },
      {
        path: 'fa/:cusip',
        redirectTo: '/aw/product-shelf/fa/:cusip',
        pathMatch: 'full',
      },
      {
        path: 'fa/:session/:org',
        redirectTo: '/aw/product-shelf/fa',
        pathMatch: 'full',
      },
      {
        path: 'fa/:session/:org/:cusip',
        redirectTo: '/aw/product-shelf/fa/:cusip',
        pathMatch: 'full',
      },
      {
        path: 'compare-results',
        component: AwComparisonComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'AW',
          breadcrumb: 'Compare Results',
        },
      },
      {
        path: 'lookup',
        component: AWProductSearchComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'AllowCurrentFactSheetLookup',
        },
      },
      {
        path: 'factsheets',
        redirectTo: '/aw/lookup',
        pathMatch: 'full',
      },
      {
        path: 'rmd-calculation',
        component: RmdCalculationComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'AW',
        },
      },
      {
        path: 'cannex-calculator',
        component: CannexCalcComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'CannexCalc',
        },
      },
      {
        path: 'cash-flow-planner',
        component: CashFlowPlannerComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'AW',
          rightNeeded: 'CashFlowPlanner',
        },
      },
      {
        path: 'hot-rates',
        component: HotRatesComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'AW',
          rightNeeded: 'HotRates',
        },
      },
      {
        path: '',
        redirectTo: '/',
        pathMatch: 'full',
      },
      // THE FOLLOWING ARE OLD ROUTES THAT HAVE BEEN
      // REDIRECTED TO THE LANDING PAGE
      {
        path: 'search',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'questionnaire',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'questionnaire/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'results/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'help',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'proxy',
        redirectTo: '/',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'pp',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: 'plan-lookup',
        component: PlanLookupComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'PP',
        },
      },
      {
        path: '',
        redirectTo: '/',
        pathMatch: 'full',
      },
      // THE FOLLOWING ARE OLD ROUTES THAT HAVE BEEN
      // REDIRECTED TO THE LANDING PAGE
      {
        path: 'search',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'questionnaire',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'questionnaire/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'results/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'help',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'proxy',
        redirectTo: '/',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'li',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        redirectTo: '/',
        pathMatch: 'full',
      },
      // THE FOLLOWING ARE OLD ROUTES THAT HAVE BEEN
      // REDIRECTED TO THE LANDING PAGE
      {
        path: 'search',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'questionnaire',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'questionnaire/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'results',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'results/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'needs',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'needs/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'compare',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'compare/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'validate',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'validate/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'products/:id',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'help',
        redirectTo: '/',
        pathMatch: 'full',
      },
      {
        path: 'proxy',
        redirectTo: '/',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: 'case-management',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        component: CaseManagementComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'CM',
          breadcrumb: 'Case Management',
        },
      },
      {
        path: 'case-manager/:CRID',
        component: CaseManagerComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'CM',
          breadcrumb: 'Case Management',
        },
      },
    ],
  },
  {
    path: 'carrier-portal',
    pathMatch: 'full',
    component: CarrierPortalComponent,
    canActivate: [AuthService],
    data: {
      rightNeeded: {
        rights: ['CarrierPortal', 'HomeOfficeCarrierPortal'],
        operation: 'or',
      },
    },
  },
  {
    path: 'carrier-policy-details',
    pathMatch: 'full',
    component: CarrierPortalComponent,
    canActivate: [AuthService],
    data: {
      rightNeeded: 'CarrierPortalPolicyDetails',
    },
  },
  {
    path: 'book-management',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: 'office-view',
        pathMatch: 'full',
        component: BookManagementComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'BookManagement',
          breadcrumb: 'Book Management',
        },
      },
      {
        path: 'rep-reports',
        pathMatch: 'full',
        component: BookManagementComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'BookManagement',
          breadcrumb: 'Book Management',
        },
      },
      {
        path: '',
        component: BookManagementComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'BookManagement',
          breadcrumb: 'Book Management',
        },
      },
      {
        path: ':id',
        component: BookManagementComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'BookManagement',
          breadcrumb: 'Book Management',
        },
      },
    ],
  },
  {
    path: 'post-trade',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        component: PostTradeComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'PostTrade',
          breadcrumb: 'Post Transaction',
        },
      },
      {
        path: 'reports',
        component: PostTradeReportsComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'PostTrade',
          breadcrumb: 'Transaction Monitor',
        },
      },
      {
        path: ':view',
        component: PostTradeComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'PostTrade',
          breadcrumb: 'Post Transaction',
        },
      },
    ],
  },
  {
    path: 'trade-review',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        component: TradeReviewComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'TradeReview',
          breadcrumb: 'Transaction Monitor',
        },
      },
      {
        path: ':id',
        component: TradeReviewComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'TradeReview',
          breadcrumb: 'Transaction Monitor',
        },
      },
    ],
  },
  {
    path: 'account-review',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        component: AccountReviewComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'AccountReview',
          breadcrumb: 'Account Monitor',
        },
      },
      {
        path: ':id',
        component: AccountReviewComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'AccountReview',
          breadcrumb: 'Account Monitor',
        },
      },
    ],
  },
  {
    path: 'rep-review',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        component: RepReviewComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'RepReview',
          breadcrumb: 'Financial Professional Monitor',
        },
      },
      {
        path: ':id',
        component: RepReviewComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'RepReview',
          breadcrumb: 'Financial Professional Monitor',
        },
      },
    ],
  },
  {
    path: 'aml-monitor',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        component: AmlMonitorComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'AML',
          breadcrumb: 'Anti-Money Laundering Monitor',
        },
      },
      {
        path: ':id',
        component: AmlMonitorComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'AML',
          breadcrumb: 'Anti-Money Laundering Monitor',
        },
      },
    ],
  },
  {
    path: 'account-grouping',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        component: AccountGroupingComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'AccountGroup',
          breadcrumb: 'Account Grouping',
        },
      },
      {
        path: ':id',
        component: AccountGroupingComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'AccountGroup',
          breadcrumb: 'Account Grouping',
        },
      },
    ],
  },
  {
    path: 'account-linking',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        component: AccountLinkingComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'AccountLinking',
          breadcrumb: 'Account Linking',
        },
      },
      {
        path: ':id',
        component: AccountLinkingComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'AccountLinking',
          breadcrumb: 'Account Linking',
        },
      },
    ],
  },
  {
    path: 'trade-source-data',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        component: TradeReviewComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'TradeReview',
          breadcrumb: 'Transaction Monitor',
        },
      },
      {
        path: ':id',
        component: TradeSourceDataComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'TradeReview',
          breadcrumb: 'Transaction Source Data',
        },
      },
    ],
  },
  {
    path: 'se',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        component: DashboardComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'SE',
        },
      },
      {
        path: 'dashboard',
        component: DashboardComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'SE',
          breadcrumb: 'Existing Case',
        },
      },
      {
        path: 'search',
        component: UniversalSearchComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'SE',
          breadcrumb: 'Existing Case',
        },
      },
      {
        path: 'search/:type',
        component: UniversalSearchComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'SE',
          breadcrumb: 'Existing Case',
        },
      },
      {
        path: 'search/:type/:id',
        component: UniversalSearchComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'SE',
          breadcrumb: 'Existing Case',
        },
      },
      {
        path: 'client/:id',
        component: ProductResultComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'SE',
          breadcrumb: 'Results',
        },
        runGuardsAndResolvers: 'always',
      },
      {
        path: 'client/:id/need-detail/:idx',
        component: ResultDetailComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'SE',
          breadcrumb: 'Results',
        },
      },
      {
        path: 'client/edit/:id',
        component: FormlyComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'SE',
          breadcrumb: 'Results',
        },
      },
      {
        path: 'proxy',
        component: ProxyComponent,
        canActivate: [AuthService],
        data: {
          appNeeded: 'SE',
        },
      },
    ],
  },
  {
    path: 'account',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/account/templates',
      },
      {
        path: 'templates',
        component: TemplatesComponent,
        canActivate: [AuthService],
        data: {
          breadcrumb: 'Templates',
        },
        resolve: {
          globals: AppGlobalsService,
        },
      },
    ],
  },
  {
    path: 'admin',
    resolve: {
      globals: AppGlobalsService,
    },
    children: [
      {
        path: '',
        redirectTo: '/unified',
        pathMatch: 'full',
      },
      {
        path: 'roles-rights',
        component: RolesRightsComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'Admin',
        },
      },
      {
        path: 'user-management',
        component: UserManagementComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'Admin',
        },
      },
      {
        path: 'unit-profile',
        component: UnitProfileComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'Admin',
        },
      },
      {
        path: 'unit-management',
        component: UnitManagementComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'Admin',
        },
      },
      {
        path: 'rep-management',
        component: RepManagementComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'Admin',
        },
      },
      {
        path: 'versions',
        component: VersionsComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'Admin',
        },
      },
      {
        path: 'uploads',
        component: UploadsComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: {
            rights: ['Admin', 'KnowledgeBase'],
            operation: 'and',
          },
        },
      },
      {
        path: 'toolbox',
        component: ToolboxComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'Admin',
        },
      },
      {
        path: 'kb-change-history',
        component: KbChangeHistoryComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'Admin',
        },
      },
      {
        path: 'content-management',
        component: ContentManagementComponent,
        canActivate: [AuthService],
        data: {
          rightNeeded: 'Admin',
        },
      },
    ],
  },
  {
    path: 'reporting',
    component: UnifiedReportingComponent,
    canActivate: [AuthService],
    data: {
      breadcrumb: 'reporting',
      rightNeeded: 'ReportingTabInMenu',
    },
    resolve: {
      globals: AppGlobalsService,
    },
  },
  {
    path: 'automated-reports',
    component: AutoReportsComponent,
    canActivate: [AuthService],
    data: {
      rightNeeded: 'automatedReportPage',
    },
  },
  {
    path: 'downloads',
    component: DownloadPageComponent,
    canActivate: [AuthService],
    data: {
      rightNeeded: 'Sensitive',
    },
    resolve: {
      globals: AppGlobalsService,
    },
  },
  {
    path: 'training',
    children: [
      {
        matcher: trainingMatcher,
        canActivate: [AuthService],
        component: TrainingFlowComponent,
      },
    ],
  },
  {
    path: 'unified',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'search',
    component: UnifiedFlowSearchComponent,
    canActivate: [AuthService],
  },
  {
    path: 'case',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'case/:app',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'case/:app/:id',
    redirectTo: 'case/:app/:id/0',
    pathMatch: 'full',
  },
  {
    path: 'case/:app/:id/:step',
    component: UnifiedCaseComponent,
    canActivate: [AuthService],
    resolve: {
      globals: AppGlobalsService,
    },
  },
  {
    path: 'case-conclusion',
    component: PostbackThankYouComponent,
    canActivate: [AuthService],
  },
  {
    path: 'completed',
    component: CustomCompletedPageComponent,
    canActivate: [AuthService],
  },
  {
    path: 'summary/proxy',
    component: ProxyComponent,
    canActivate: [AuthService],
  },
  {
    path: 'proxy',
    component: ProxyComponent,
    canActivate: [AuthService],
  },
  {
    path: 'tools',
    component: ToolsPageComponent,
    canActivate: [AuthService],
    data: {
      rightNeeded: {
        rights: ['ToolsLandingPage', 'AccessToolsPage'],
        operation: 'or',
      },
    },
  },
  {
    path: 'settings',
    component: UserSettingsComponent,
    canActivate: [AuthService],
  },
  {
    path: 'cdrnotrequired',
    pathMatch: 'full',
    component: NoCdrRequiredComponent,
  },
  {
    path: 'not-allowed',
    pathMatch: 'full',
    component: PageNotAllowedComponent,
  },
  {
    path: '**',
    pathMatch: 'full',
    component: PageNotFoundComponent,
  },
];

export const routerConfig = {
  useHash: false,
  onSameUrlNavigation: 'reload' as const,
  anchorScrolling: 'enabled' as const,
  scrollOffset: [0, 64] as [number, number],
};
